import React from 'react';

import {
  Box,
  ColDefBuilder,
  DataTable,
  HStack,
  Heading,
  Text,
  VStack,
} from '@eluve/components';

import { EditVariantDialog } from './EditVariantDialog';
import { SyncFromPostHogDialog } from './SyncFromPostHogDialog';
import { VariantConfigDisplay } from './VariantConfigDisplay';

type Variant = {
  id: string;
  experimentId: string;
  name: string;
  posthogVariantKey: string;
  variantConfig: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

interface ExperimentVariantsListProps {
  experimentId: string;
  variants: Variant[];
  isActivated: boolean;
  posthogFlagKey: string;
}

type VariantRow = {
  id: string;
  name: string;
  posthogVariantKey: string;
  variantConfig: Record<string, unknown>;
  createdAt: string;
};

export const ExperimentVariantsList: React.FC<ExperimentVariantsListProps> = ({
  experimentId,
  variants,
  isActivated,
  posthogFlagKey,
}) => {
  const rows: VariantRow[] = variants.map((v) => ({
    id: v.id,
    name: v.name,
    posthogVariantKey: v.posthogVariantKey,
    variantConfig: v.variantConfig as Record<string, unknown>,
    createdAt: v.createdAt,
  }));

  const colsWithActions = new ColDefBuilder<VariantRow>()
    .defaultSortable('name', 'Name')
    .defaultSortable('posthogVariantKey', 'PostHog Variant Key')
    .colDef({
      header: 'Config',
      cell: ({ row }) => (
        <VariantConfigDisplay config={row.original.variantConfig} />
      ),
    })
    .dateSortable('createdAt', 'Created At')
    .colDef({
      header: 'Actions',
      cell: ({ row }) => (
        <EditVariantDialog
          experimentId={experimentId}
          variantId={row.original.id}
          defaultValues={{
            name: row.original.name,
            posthogVariantKey: row.original.posthogVariantKey,
            variantConfig: JSON.stringify(row.original.variantConfig, null, 2),
          }}
        />
      ),
    })
    .build();

  return (
    <VStack gap={4}>
      <HStack justify="between" align="center">
        <Heading size="lg">Variants</Heading>
        <SyncFromPostHogDialog
          experimentId={experimentId}
          posthogFlagKey={posthogFlagKey}
          existingVariants={variants.map((v) => ({
            id: v.id,
            posthogVariantKey: v.posthogVariantKey,
          }))}
          isActivated={isActivated}
        />
      </HStack>

      {variants.length === 0 ? (
        <Box className="rounded-lg border border-dashed border-border-primary p-6 text-center">
          <Text color="tertiary">
            No variants yet. Use "Sync from PostHog" to import variants from
            your feature flag.
          </Text>
        </Box>
      ) : (
        <DataTable
          data={rows}
          columns={colsWithActions}
          isPaginated={false}
          initialSortingState={[{ id: 'createdAt', desc: false }]}
        />
      )}
    </VStack>
  );
};
